import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { withAuthenticationRequired } from '../providers/withAuthenticationRequired';
import { useAuth } from '../hooks/useAuth';
import { Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import toast from 'react-hot-toast';
import MonitoringListSelect from '../Components/FormComponents/MonitoringListSelect';
import RegistrationCarousel from '../Components/RegistrationReview/RegistrationCarousel';
import BusinessConfirmModal from '../Components/RegistrationReview/BusinessConfirmModal';
import { useRegistrationReviewQueueQuery } from '../queries/registrationReview.query';
import { useClassificationReviewMutation } from '../mutations/registrationReview.mutation';
import { PlateClassification } from '../Types';
import {
  ClassificationReviewResult,
  NoticeChannelResult,
  RegistrationReviewItem
} from '../types/registrationReview.types';

const formatDate = (value: string | null) =>
  value
    ? new Date(value).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    : '';

const describeChannel = (channel: 'Text' | 'Email', result: NoticeChannelResult | null) => {
  if (!result) return `${channel}: not attempted`;
  if (result.status === 'Sent') return `${channel} sent to ${result.recipient}`;
  if (result.status === 'NoRecipient') return `${channel} not sent: ${result.error}`;
  return `${channel} FAILED${result.recipient ? ` (${result.recipient})` : ''}: ${result.error}`;
};

/** Tell the reviewer exactly what reached the client, and make failures hard to miss. */
const announceResult = (result: ClassificationReviewResult) => {
  if (result.classification === PlateClassification.Personal) {
    toast.success(`${result.plateNumber} marked Personal`);
    return;
  }

  const notice = result.notice;
  if (!notice.attempted) {
    toast.success(
      `${result.plateNumber} marked Business. Client was already notified on ${formatDate(
        notice.alreadySentAt
      )}.`
    );
    return;
  }

  const lines = [describeChannel('Text', notice.sms), describeChannel('Email', notice.email)];
  const allSent = notice.sms?.status === 'Sent' && notice.email?.status === 'Sent';
  const body = (
    <div>
      <strong>{result.plateNumber} marked Business</strong>
      {lines.map((line) => (
        <div key={line} className="small">
          {line}
        </div>
      ))}
    </div>
  );

  if (allSent) {
    toast.success(body, { duration: 6000 });
  } else {
    toast.error(body, { duration: 15000 });
  }
};

function RegistrationsPage() {
  const { user } = useAuth();
  const [monitoringListId, setMonitoringListId] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [includeReviewed, setIncludeReviewed] = useState(false);

  const { data, isLoading, isFetching, refetch } = useRegistrationReviewQueueQuery({
    monitoringListId: monitoringListId || undefined,
    includeReviewed
  });
  const reviewMutation = useClassificationReviewMutation();

  // Working copy: reviewed plates drop out locally so the carousel moves straight on to the
  // next registration without waiting for a refetch.
  const [items, setItems] = useState<RegistrationReviewItem[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [reviewedCount, setReviewedCount] = useState(0);
  const [pendingBusiness, setPendingBusiness] = useState<RegistrationReviewItem | null>(null);

  useEffect(() => {
    setItems(data ?? []);
    setActiveIndex(0);
    setReviewedCount(0);
  }, [data]);

  const clientOptions = useMemo(() => {
    const seen = new Map<string, string>();
    items.forEach((item) => seen.set(item.clientId, item.clientName));
    return Array.from(seen.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  const visibleItems = useMemo(
    () => (clientFilter ? items.filter((item) => item.clientId === clientFilter) : items),
    [items, clientFilter]
  );

  useEffect(() => {
    if (activeIndex > visibleItems.length - 1) {
      setActiveIndex(Math.max(0, visibleItems.length - 1));
    }
  }, [activeIndex, visibleItems.length]);

  const decide = useCallback(
    async (item: RegistrationReviewItem, classification: PlateClassification) => {
      let result: ClassificationReviewResult | undefined;
      try {
        result = await reviewMutation.mutateAsync({
          plateId: item.plateId,
          clientId: item.clientId,
          request: {
            classification,
            registrationFileId: item.fileId,
            reviewerName: user?.displayName || user?.email
          }
        });
      } catch {
        // The API interceptor has already shown the error; leave the plate in the queue.
        return;
      }
      if (!result) return;
      const saved = result;

      announceResult(saved);
      setReviewedCount((count) => count + 1);

      if (includeReviewed) {
        setItems((previous) =>
          previous.map((existing) =>
            existing.plateId === item.plateId
              ? {
                  ...existing,
                  classification: saved.classification,
                  classificationReviewedAt: saved.reviewedAt,
                  classificationReviewedByName: saved.reviewedByName,
                  businessNoticeSentAt: saved.businessNoticeSentAt
                }
              : existing
          )
        );
        setActiveIndex((index) => Math.min(index + 1, visibleItems.length - 1));
      } else {
        // The next plate slides into the same index.
        setItems((previous) => previous.filter((existing) => existing.plateId !== item.plateId));
      }
    },
    [reviewMutation, user, includeReviewed, visibleItems.length]
  );

  const handlePersonal = useCallback(
    (item: RegistrationReviewItem) => decide(item, PlateClassification.Personal),
    [decide]
  );

  const handleBusiness = useCallback(
    (item: RegistrationReviewItem) => setPendingBusiness(item),
    []
  );

  const confirmBusiness = async () => {
    if (!pendingBusiness) return;
    await decide(pendingBusiness, PlateClassification.Business);
    setPendingBusiness(null);
  };

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-start">
        <div>
          <h3>Registration Review</h3>
          <p className="text-muted">
            Look at each registration and mark whether the plate is registered to a person or a
            corporation.
          </p>
        </div>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}>
          {isFetching ? <Spinner animation="border" size="sm" /> : 'Reload queue'}
        </Button>
      </div>

      <Row className="g-3 mb-3 align-items-end">
        <Col md={4}>
          <label className="fw-bold small mb-1">Monitoring List</label>
          <MonitoringListSelect
            handleDataChange={(_key, value) => {
              setClientFilter('');
              setMonitoringListId(value);
            }}
            value={monitoringListId}
            addNew={false}
            showAllOption
          />
        </Col>
        <Col md={4}>
          <label className="fw-bold small mb-1">Client</label>
          <Form.Select
            value={clientFilter}
            onChange={(e) => {
              setClientFilter(e.target.value);
              setActiveIndex(0);
            }}>
            <option value="">All clients ({clientOptions.length})</option>
            {clientOptions.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={4}>
          <Form.Check
            type="switch"
            id="include-reviewed"
            label="Include plates already reviewed"
            checked={includeReviewed}
            onChange={(e) => {
              setClientFilter('');
              setIncludeReviewed(e.target.checked);
            }}
          />
        </Col>
      </Row>

      {isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : visibleItems.length === 0 ? (
        <Card body className="text-center py-5">
          <h5 className="text-muted mb-1">Nothing left to review.</h5>
          <div className="text-muted small">
            {reviewedCount > 0
              ? `You reviewed ${reviewedCount} plate${reviewedCount === 1 ? '' : 's'}.`
              : 'Every plate with a registration on file has been reviewed.'}
          </div>
        </Card>
      ) : (
        <RegistrationCarousel
          items={visibleItems}
          activeIndex={Math.min(activeIndex, visibleItems.length - 1)}
          reviewedCount={includeReviewed ? 0 : reviewedCount}
          busy={reviewMutation.isLoading}
          keyboardEnabled={!pendingBusiness}
          onIndexChange={setActiveIndex}
          onPersonal={handlePersonal}
          onBusiness={handleBusiness}
        />
      )}

      <BusinessConfirmModal
        item={pendingBusiness}
        submitting={reviewMutation.isLoading}
        onCancel={() => setPendingBusiness(null)}
        onConfirm={confirmBusiness}
      />
    </Container>
  );
}

export default withAuthenticationRequired(RegistrationsPage);
